import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Match-day card modal — generates, previews, and shares the 1080x1920 SVG.
 *
 * Coaches additionally get a "Send to team" button that:
 *   1. Mints a short-lived public URL for the card.
 *   2. POSTs to /coach/whatsapp/send-card which fans the image out to
 *      every parent on the coach's roster via the WhatsApp Business
 *      Cloud API.
 *   The button only appears when the caller is a coach (we check
 *   /coach/whatsapp/config returning { configured: true }).
 *
 * Share surfaces, in order of preference:
 *
 *   1. Web Share API with the PNG as a File payload — works on iOS
 *      Safari 16.4+ and recent Android Chrome. Opens the OS share
 *      sheet with WhatsApp/IG already present.
 *   2. WhatsApp deep link — opens WhatsApp with a prefilled caption.
 *      We copy the PNG to the clipboard at the same time so the user
 *      can paste it into the chat in one tap. Works on every platform.
 *   3. Instagram Stories scheme — `instagram-stories://share` (iOS)
 *      or `intent://share?source_application=...#Intent` (Android).
 *      We fall back gracefully when the app isn't installed.
 *   4. "Save image" — always available, the universal fallback.
 *
 * We rasterize to PNG once per session and cache it in a ref. SVG-to-
 * PNG via <canvas> is a few hundred ms on a phone, so we eat it
 * eagerly while the user reads the preview.
 */
export default function MatchCard({ sessionId, onClose }) {
  const { token, user } = useAuth();
  const [svg, setSvg] = useState(null);
  const [tagline, setTagline] = useState('');
  const [loading, setLoading] = useState(true);
  const [rasterizing, setRasterizing] = useState(false);
  const [wabaConfigured, setWabaConfigured] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const pngBlobRef = useRef(null);
  const pngUrlRef = useRef(null);
  const isCoach = user?.cricket_role === 'coach';

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    axios.get(`${API}/match-card/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      setSvg(res.data.svg);
      setTagline(res.data.tagline);
    }).catch(() => {
      toast.error('Could not generate match card');
    }).finally(() => setLoading(false));
  }, [sessionId, token]);

  // Coaches: probe WABA config so we know whether to show "Send to team".
  useEffect(() => {
    if (!isCoach || !token) return;
    axios.get(`${API}/coach/whatsapp/config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setWabaConfigured(!!r.data?.configured))
      .catch(() => setWabaConfigured(false));
  }, [isCoach, token]);

  const sendToTeam = async () => {
    if (!sessionId) return;
    setBlasting(true);
    try {
      // 1. Mint a public URL for the card.
      const mint = await axios.post(
        `${API}/match-card/${sessionId}/public-url`, {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // 2. Send to roster via WABA.
      const blast = await axios.post(
        `${API}/coach/whatsapp/send-card`,
        {
          image_url: mint.data.url,
          caption: `${tagline}\n\nvia Cricket Refreshed`,
          match_card_session_id: sessionId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Sent to ${blast.data.sent}/${blast.data.recipients} parents`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not blast card');
    } finally {
      setBlasting(false);
    }
  };

  // Rasterize as soon as the SVG arrives so share buttons are instant.
  useEffect(() => {
    if (!svg || pngBlobRef.current) return;
    setRasterizing(true);
    rasterize(svg)
      .then((blob) => {
        pngBlobRef.current = blob;
        pngUrlRef.current = URL.createObjectURL(blob);
      })
      .catch(() => {})
      .finally(() => setRasterizing(false));
    return () => {
      if (pngUrlRef.current) URL.revokeObjectURL(pngUrlRef.current);
      pngUrlRef.current = null;
      pngBlobRef.current = null;
    };
  }, [svg]);

  const pngFile = useCallback(() => {
    if (!pngBlobRef.current) return null;
    return new File(
      [pngBlobRef.current],
      `cricket-refreshed-${sessionId}.png`,
      { type: 'image/png' },
    );
  }, [sessionId]);

  // ----- share handlers -----

  const downloadPng = async () => {
    if (!pngBlobRef.current && svg) {
      const blob = await rasterize(svg);
      pngBlobRef.current = blob;
      pngUrlRef.current = URL.createObjectURL(blob);
    }
    if (!pngUrlRef.current) return;
    const dl = document.createElement('a');
    dl.href = pngUrlRef.current;
    dl.download = `cricket-refreshed-${sessionId}.png`;
    dl.click();
  };

  const nativeShare = async () => {
    const file = pngFile();
    if (!file || !navigator.share) {
      downloadPng();
      toast.info('Saved — paste it into any chat.');
      return;
    }
    try {
      // Some browsers refuse files with `text` — we try with files first,
      // then degrade.
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: tagline });
      } else {
        await navigator.share({ text: tagline });
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        downloadPng();
        toast.info('Saved — paste it into any chat.');
      }
    }
  };

  const shareToWhatsApp = async () => {
    // 1. Copy PNG to clipboard so the user can paste in WhatsApp.
    await copyImageToClipboard(pngBlobRef.current, svg);
    // 2. Open WhatsApp with a prefilled caption.
    const text = encodeURIComponent(`${tagline}\n\nvia Cricket Refreshed`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
    toast.success('Image copied — paste it in WhatsApp');
  };

  const shareToInstagram = async () => {
    // Web → Instagram Stories doesn't have a clean cross-browser deep
    // link. Best we can do: copy the image + open the Instagram website,
    // tell the user the rest.
    await copyImageToClipboard(pngBlobRef.current, svg);
    // iOS: try the official sticker URL scheme (works inside Safari
    // when Instagram is installed).
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua);
    if (isIos) {
      window.location.href = 'instagram-stories://share?source_application=cricketrefreshed';
      setTimeout(() => {
        toast.info('If Instagram didn\'t open, paste from clipboard into a Story.');
      }, 800);
    } else {
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
      toast.info('Image copied — paste into your Story.');
    }
  };

  const shareToTwitter = () => {
    const text = encodeURIComponent(`${tagline}\n\nvia Cricket Refreshed`);
    window.open(
      `https://twitter.com/intent/tweet?text=${text}`,
      '_blank', 'noopener,noreferrer',
    );
    copyImageToClipboard(pngBlobRef.current, svg).then(() => {
      toast.info('Image copied — paste into the tweet composer.');
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-4 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0a0e27',
          border: '1px solid rgba(0,201,167,0.3)',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-white">Match card</div>
            <div className="text-xs text-slate-400">Vertical · 1080×1920</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">✕</button>
        </div>

        <div
          className="rounded-xl overflow-hidden mb-3"
          style={{ aspectRatio: '9 / 16', background: '#000' }}
        >
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm">
              Generating card…
            </div>
          ) : (
            <div
              className="w-full h-full"
              style={{ display: 'flex' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svg || '', { USE_PROFILES: { svg: true, svgFilters: true } }) }}
            />
          )}
        </div>

        <div className="grid grid-cols-4 gap-2 mb-2">
          <ShareButton
            label="WhatsApp" color="#25D366" emoji="💬"
            onClick={shareToWhatsApp} disabled={loading}
          />
          <ShareButton
            label="Instagram" color="#E4405F" emoji="📸"
            onClick={shareToInstagram} disabled={loading}
          />
          <ShareButton
            label="X" color="#000000" emoji="𝕏"
            onClick={shareToTwitter} disabled={loading}
          />
          <ShareButton
            label="More" color="rgba(255,255,255,0.12)" emoji="↗"
            onClick={nativeShare} disabled={loading}
          />
        </div>

        <button
          onClick={downloadPng}
          disabled={loading}
          className="w-full py-2.5 rounded-lg font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
        >
          {rasterizing ? 'Preparing image…' : 'Save image'}
        </button>

        {isCoach && wabaConfigured && (
          <button
            onClick={sendToTeam}
            disabled={loading || blasting}
            className="w-full mt-2 py-2.5 rounded-lg font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
          >
            {blasting ? 'Blasting to roster…' : '📣 Send to team (WhatsApp)'}
          </button>
        )}

        <p className="text-[11px] text-slate-500 mt-2 text-center">
          We copy the image to your clipboard so you can paste it directly
          into any chat or Story.
        </p>
      </div>
    </div>
  );
}

function ShareButton({ label, color, emoji, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg flex flex-col items-center justify-center gap-1 py-2 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"
      style={{
        background: color === 'rgba(255,255,255,0.12)' ? color : `${color}cc`,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1 }}>{emoji}</span>
      <span>{label}</span>
    </button>
  );
}

// ---------- helpers ----------

function rasterize(svg) {
  return new Promise((resolve, reject) => {
    try {
      const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 1080;
        canvas.height = 1920;
        canvas.getContext('2d').drawImage(img, 0, 0, 1080, 1920);
        canvas.toBlob((png) => {
          URL.revokeObjectURL(url);
          if (png) resolve(png);
          else reject(new Error('toBlob_failed'));
        }, 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('img_load_failed'));
      };
      img.src = url;
    } catch (err) { reject(err); }
  });
}

async function copyImageToClipboard(blob, svgFallback) {
  let pngBlob = blob;
  if (!pngBlob && svgFallback) {
    try { pngBlob = await rasterize(svgFallback); } catch { /* ignore */ }
  }
  if (!pngBlob || !navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    return false;
  }
  try {
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': pngBlob }),
    ]);
    return true;
  } catch (_) {
    return false;
  }
}
